import React, { ChangeEvent, useEffect, useState } from "react";
import {
  SelectChangeEvent,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  Button,
  Chip,
  FormHelperText,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useAddNotificationMutation,
  useUpdateNotificationMutation,
} from "../../services/notification";
import { showError } from "../../helpers";
import { useLazyGetUserListQuery } from "../../services/user";
import dayjs, { Dayjs } from "dayjs";
import {
  NOTIFICATION_SCHEDULE_TYPE,
  NOTIFICATION_TYPE,
} from "../../constants/enums";
import EditText from "../../components/editText";

const usersList: { label: string; id: string }[] = [
  { label: "Select All Users", id: "all" },
];

const AddNotification = () => {
  const navigate = useNavigate();

  const [alertMessage, setAlertMessage] = React.useState("");
  const [showAlert, setShowAlert] = React.useState(false);
  const [alertType, setAlertType] = React.useState(0);
  // removed image upload; message will be sent in content
  const [title, setTitle] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>("");
  const [previewText, setPreviewText] = React.useState<string>("");
  const [messageError, setMessageError] = React.useState<string>("");
  const [linkUrl, setLinkUrl] = React.useState<string>("");

  const [addNotification, { isLoading: isAdding }] =
    useAddNotificationMutation();
  const [updatedNotification] = useUpdateNotificationMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(true);
  const [selectedUsers, setSelectedUsers] = React.useState<typeof usersList>(
    []
  );
  const [type, setType] = React.useState("default");
  const [schedule, setSchedule] = React.useState("default");
  const [link, setLink] = React.useState("default");
  const [rewards, setRewards] = React.useState("default");
  const [scheduleDateTime, setScheduleDateTime] = React.useState<Dayjs | null>(
    null
  );

  const handleUsersChange = (event: any, value: typeof usersList) => {
    const clickedAll = value.some((user) => user.id === "all");
    const currentlyAll = selectedUsers.some((u) => u.id === "all");

    if (clickedAll) {
      // Toggle select-all: if already all, clear; else set only the 'all' chip
      if (currentlyAll) {
        setSelectedUsers([]);
      } else {
        setSelectedUsers([{ label: "Select All Users", id: "all" }] as any);
      }
      return;
    }

    // If not clicking 'all', and 'all' is active, replace with the new specific selection
    if (currentlyAll) {
      setSelectedUsers(value.filter((v) => v.id !== "all"));
    } else {
      setSelectedUsers(value);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value as string);
  };
  const handleScheduleChange = (event: SelectChangeEvent) => {
    setSchedule(event.target.value as string);
  };
  const handleLinkChange = (event: SelectChangeEvent) => {
    setLink(event.target.value as string);
  };
  const handleRewardsChange = (event: SelectChangeEvent) => {
    setRewards(event.target.value as string);
  };

  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [UsersData, setUsersData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [getUserList] = useLazyGetUserListQuery();

  const fetchList = async () => {
    try {
      const params: any = {
        page: page,
        limit: 1000,
        search: debouncedSearchTerm,
        isBlocked: 1,
        userType: "",
      };
      const response = await getUserList(params).unwrap();
      if (response?.statusCode === 200) {
        setUsersData(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Users");
    }
  };
  useEffect(() => {
    fetchList();
  }, []);

  const mappedUserOptions: { label: string; id: string }[] =
    React.useMemo(() => {
      const dynamicUsers = (UsersData || [])
        .map((u: any) => ({
          id: u?._id || u?.id || "",
          label: `${u?.fullName || u?.name || u?.firstName || ""}${
            u?.email ? ", " + u?.email : ""
          }`.trim(),
        }))
        .filter((o) => o.id);
      return [{ label: "Select All Users", id: "all" }, ...dynamicUsers];
    }, [UsersData]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Validation
    const trimmedTitle = title?.trim() || "";
    if (!trimmedTitle) {
      showError("Notification Title is required");
      return;
    }

    if (type === "default") {
      showError("Please select a Type");
      return;
    }

    if (selectedUsers.length === 0) {
      showError("Please select at least one Audience");
      return;
    }

    if (schedule === "default") {
      showError("Please select a Schedule");
      return;
    }

    if (schedule === "2" && !scheduleDateTime) {
      showError("Please select Date & Time for scheduled notification");
      return;
    }

    const trimmedPreviewText = previewText?.trim() || "";
    const trimmedMessage = message?.trim() || "";
    if (!trimmedMessage) {
      setMessageError("Notification Message is required");
      showError("Notification Message is required");
      return;
    }
    setMessageError("");

    if (link === "3") {
      const trimmedLinkUrl = linkUrl?.trim() || "";
      if (!trimmedLinkUrl) {
        showError("Please enter a URL");
        return;
      }
    }

    try {
      const isAllSelected = selectedUsers.some((u) => u.id === "all");
      const selectedIds = isAllSelected
        ? (UsersData || []).map((u: any) => u?._id || u?.id).filter(Boolean)
        : selectedUsers.filter((u) => u.id !== "all").map((u) => u.id);
      const isMultiReceivers = true;
      const notificationType =
        type === "email" ? NOTIFICATION_TYPE.EMAIL : NOTIFICATION_TYPE.PUSH;
      const isBroadcast = isAllSelected;
      const scheduleType =
        schedule === "2"
          ? NOTIFICATION_SCHEDULE_TYPE.LATER
          : NOTIFICATION_SCHEDULE_TYPE.NOW;
      const scheduleAt =
        scheduleType === 2 && scheduleDateTime
          ? scheduleDateTime.toISOString()
          : "";

      const body: any = {
        title: trimmedTitle,
        content: trimmedMessage,
        ...(type !== "email" && { previewText: trimmedPreviewText }),
        notificationType,
        isBroadcast,
        userList: selectedIds,
        isMultiReceivers,
        scheduleType,
      };
      if (scheduleType === 2 && scheduleAt) {
        body.scheduleAt = scheduleAt;
      }
      if (link === "3" && linkUrl?.trim()) {
        body.linkUrl = linkUrl.trim();
      }
      console.log("Notification body:", body);
      await addNotification(body as any).unwrap();
      navigate("/manage-notifications");
    } catch (error: any) {
      showError(error?.data?.message || "Failed to create notification");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <div className="main_title">
        <h1>Add Notification</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-notifications")}>
            Notification & Communication Center
          </button>{" "}
          - Add Notification
        </p>
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSubmitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="cards">
        <form
          className="form"
          action=""
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="gap_p">
            {/* Image upload removed per spec; message goes in content */}
            <div className="control_group w_50">
              <label htmlFor="noti_title">Notification Title *</label>
              <TextField
                id="noti_title"
                placeholder="Enter Notification Title"
                fullWidth
                hiddenLabel
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="control_group w_50">
              <label htmlFor="noti_type">Type *</label>
              <Select
                id="noti_type"
                fullWidth
                value={type}
                onChange={handleTypeChange}
              >
                <MenuItem disabled value="default">
                  Select Type
                </MenuItem>
                <MenuItem value="broadcast">Broadcast</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </div>
            {type !== "email" && (
              <div className="control_group w_50">
                <label htmlFor="preview_text">Preview Text *</label>
                <TextField
                  id="preview_text"
                  placeholder="Enter Preview Text"
                  fullWidth
                  hiddenLabel
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                />
              </div>
            )}
            <div className="control_group w_100">
              <label htmlFor="noti_audience">Audience *</label>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={mappedUserOptions}
                value={selectedUsers}
                onChange={handleUsersChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onOpen={() => setAutoCompleteOpen(true)}
                onClose={() => setAutoCompleteOpen(false)}
                filterSelectedOptions
                renderTags={(value, getTagProps) => {
                  const isAll = value.some((v) => v.id === "all");
                  if (isAll) {
                    return [
                      <Chip
                        {...getTagProps({ index: 0 })}
                        key="all-chip"
                        label="All Users"
                      />,
                    ];
                  }
                  return value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                    />
                  ));
                }}
                renderOption={(props, option, { selected }) => {
                  const isAllOption = option.id === "all";
                  const isAllSelected =
                    selectedUsers.length === mappedUserOptions.length - 1;
                  return (
                    <li {...props}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              isAllOption
                                ? selectedUsers.some((u) => u.id === "all")
                                : selected
                            }
                            indeterminate={
                              isAllOption &&
                              selectedUsers.length > 0 &&
                              !isAllSelected
                            }
                          />
                        }
                        name="option"
                        label={option.label}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="noti_audience"
                    placeholder="Select Audience"
                  />
                )}
              />
            </div>
            <div className="control_group w_50">
              <label htmlFor="noti_schedule">Schedule</label>
              <Select
                id="noti_schedule"
                fullWidth
                value={schedule}
                onChange={handleScheduleChange}
              >
                <MenuItem disabled value="default">
                  Select Schedule
                </MenuItem>
                <MenuItem value="1">Now</MenuItem>
                <MenuItem value="2">Later</MenuItem>
              </Select>
            </div>
            {schedule === "2" && (
              <div className="control_group w_50">
                <label htmlFor="noti_datetime"> Date & Time</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    className="form_control"
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/date_icon.svg"
                          alt="Pick date"
                        />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        id: "noti_datetime",
                        placeholder: "Select Date & Time",
                        fullWidth: true,
                        hiddenLabel: true,
                        inputProps: {
                          "aria-label": "Select Date & Time",
                        },
                      },
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                    minDateTime={dayjs().startOf("minute")}
                    value={scheduleDateTime}
                    onChange={(v) => setScheduleDateTime(v)}
                  />
                </LocalizationProvider>
              </div>
            )}
            <div className="control_group w_100">
              <label htmlFor="message">Notification Message *</label>
              <EditText
                content={message || ""}
                setContent={(val: string) => {
                  setMessage(val);
                  if (messageError) {
                    setMessageError("");
                  }
                }}
                direction="ltr"
                textAlign="left"
              />
              {messageError && (
                <FormHelperText error>{messageError}</FormHelperText>
              )}
            </div>

            {/* <div className="control_group w_50">
              <label htmlFor="noti_link">Link Action</label>
              <Select
                id="noti_link"
                fullWidth
                value={link}
                onChange={handleLinkChange}>
                <MenuItem disabled value="default">Select Link Action</MenuItem>
                <MenuItem value="1">Open App</MenuItem>
                <MenuItem value="2">Redirect to Reward</MenuItem>
                <MenuItem value="3">Open URL</MenuItem>
              </Select>
            </div> */}
            {/* {link === "2" && (
              <div className="control_group w_50">
                <label htmlFor="noti_rewards">Select Reward</label>
                <Select
                  id="noti_rewards"
                  fullWidth
                  value={rewards}
                  onChange={handleRewardsChange}>
                  <MenuItem disabled value="default">Select Rewards</MenuItem>
                  <MenuItem value="1">Rewards 1</MenuItem>
                  <MenuItem value="2">Rewards 2</MenuItem>
                  <MenuItem value="3">Rewards 3</MenuItem>
                </Select>
              </div>
            )}
            {link === "3" && (
              <div className="control_group w_50">
                <label htmlFor="noti_url">Enter URL</label>
                <TextField
                  id="noti_url"
                  placeholder="Enter URL"
                  fullWidth
                  hiddenLabel
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
            )} */}
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" onClick={handleSubmit}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddNotification;
